import classnames from 'classnames';
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import type { BodyPartModifier, WieldedItemTier } from '@sorrem/shared';
import { CharacterPreviewCanvas } from '@sorrem-client/ui/features/Character/CharacterPreviewCanvas';
import { usePrefersReducedMotion } from '@sorrem-client/ui/hooks';
import { useValueStoreSelector } from '@sorrem-client/ui/hooks';
import { createValueStore, type ValueStore } from '@sorrem-client/ui/shared/value-store';
import { useCharacterHomeScene, type CharacterHomeSceneRect } from '../../hooks';
import type { CharacterHomeTab } from '../../tabs/character-home-tabs';
import styles from './CharacterHomeViewport.module.scss';

interface CharacterHomeViewportProps {
    characterId: string;
    equippedItemIds: string[];
    outfitModifiers?: Partial<Record<string, BodyPartModifier>>;
    /** Each wielded item's rarity and upgrade level, for its effect (#287). */
    itemTiers?: Readonly<Record<string, WieldedItemTier>>;
    /** A character is being created: its gear changes often as the player tries looks on, so it swaps live instead of reloading the character. */
    isCreating: boolean;
    /** Whether the 3D home moves ("Character Home effects" is All) - applies during character creation too: when off, the creation preview only repaints on a character/outfit/camera change instead of playing its idle animation continuously. */
    animationsEnabled?: boolean;
    /** The tab under the pointer; its prop in the room highlights. Read here, so a hover re-renders only the room. */
    highlightedTabStore?: ValueStore<CharacterHomeTab | null>;
    /** Whether the hovered tab highlights at all (not while a character is being created). */
    highlightsTabs?: boolean;
    /** Fires once, the first time the room's paintings have decoded and the character has loaded - see `CharacterPreviewCanvas`'s `onReady`. */
    onReady?: () => void;
    /**
     * Flies the view through the room's window and out of the room, into the
     * Veil sky beyond it (the Skills tab), or back. The room stays mounted, so
     * coming back never reloads it.
     */
    zoomedIntoWindow?: boolean;
    /** Called once a zoom into or out of the window has finished, with where it ended. */
    onZoomedIntoWindowChange?: (zoomedIn: boolean) => void;
    /**
     * Flies the view onto the writing table and settles on its top seen from
     * above (the Journey tab, whose books lie open on it), or back.
     */
    zoomedIntoTable?: boolean;
    className?: string;
}

/** Stands in when nothing reports a hovered tab. */
const NO_HIGHLIGHTED_TAB: ValueStore<CharacterHomeTab | null> = createValueStore<CharacterHomeTab | null>(null);

/** How long the zoom through the window takes. */
const WINDOW_ZOOM_MS = 1200;
/** With reduced motion there's no zoom, only a quicker fade. */
const REDUCED_MOTION_FADE_MS = 400;
/** How much bigger than just covering the screen the window grows, so its frame has passed out of view. */
const WINDOW_ZOOM_OVERSHOOT = 1.05;

/** What the view zooms into: the window (Skills) or the writing table (Journey). */
type ZoomTarget = 'window' | 'table';

/** Places a layer over its rect of the room painting. */
function rectStyle(rect: CharacterHomeSceneRect): CSSProperties {
    return {
        left: `${rect.left * 100}%`,
        top: `${rect.top * 100}%`,
        width: `${rect.width * 100}%`,
        height: `${rect.height * 100}%`,
    };
}

/**
 * The character standing in their room, as a full-screen background behind
 * the rest of Character Home: the painted room (see `useCharacterHomeScene`)
 * as page images, with the character's transparent 3D canvas in front.
 * The paintings and the canvas share one box that covers the screen at the
 * painting's aspect, so the character keeps their place on the painted floor
 * at any window size.
 *
 * The Journey tab zooms onto the writing table instead, and settles on its
 * top painted from above, where the Journey's books lie open.
 *
 * The room's window glass is transparent: the Veil sky behind it is the
 * Skills tab's own sky layer, drawn under this one, so zooming through the
 * window arrives in the very sky the skill tree opens over.
 *
 * Rendered exactly once, for Character Home's whole lifetime (see
 * `CharacterHome.tsx`) - switching tabs or entering character creation only
 * ever changes which `characterId`/`equippedItemIds`/`outfitModifiers` this
 * same mounted canvas is showing, never remounts it, so the camera never
 * resets. Camera angle/distance and the light on the character are set on
 * the `home-workshop` backdrop in `preview-backdrops.ts`.
 */
export function CharacterHomeViewport({
    characterId,
    equippedItemIds,
    outfitModifiers,
    itemTiers,
    isCreating,
    animationsEnabled = true,
    highlightedTabStore = NO_HIGHLIGHTED_TAB,
    highlightsTabs = true,
    onReady,
    zoomedIntoWindow = false,
    onZoomedIntoWindowChange,
    zoomedIntoTable = false,
    className,
}: CharacterHomeViewportProps): JSX.Element {
    const scene = useCharacterHomeScene();
    const hoveredTab = useValueStoreSelector(highlightedTabStore, (tab) => tab);
    const highlightedTab = highlightsTabs ? hoveredTab : null;
    const [imagesReady, setImagesReady] = useState(false);
    const [characterReady, setCharacterReady] = useState(false);
    const handleCharacterReady = useCallback((): void => setCharacterReady(true), []);
    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const hasFiredReadyRef = useRef(false);
    const reducedMotion = usePrefersReducedMotion();
    const zoomMs = reducedMotion ? REDUCED_MOTION_FADE_MS : WINDOW_ZOOM_MS;
    const onZoomChangeRef = useRef(onZoomedIntoWindowChange);
    onZoomChangeRef.current = onZoomedIntoWindowChange;
    const isFirstZoomRef = useRef(true);

    // Decode every painting up front so the room never assembles piece by
    // piece behind the loading message; a failed image still lets it show.
    useEffect(() => {
        let cancelled = false;
        const urls = [scene.roomImageUrl, scene.tableTopImageUrl, ...scene.props.map((prop) => prop.imageUrl)];
        void Promise.allSettled(urls.map((url) => {
            const image = new Image();
            image.src = url;
            return image.decode();
        })).then(() => {
            if (!cancelled) setImagesReady(true);
        });
        return (): void => {
            cancelled = true;
        };
    }, [scene]);

    useEffect(() => {
        if (hasFiredReadyRef.current || !imagesReady || !characterReady) return;
        hasFiredReadyRef.current = true;
        onReadyRef.current?.();
    }, [imagesReady, characterReady]);

    // Reports where a zoom ended once it has run its course. On mount the
    // room starts where it is asked to be, with nothing to wait for.
    useEffect(() => {
        if (isFirstZoomRef.current) {
            isFirstZoomRef.current = false;
            onZoomChangeRef.current?.(zoomedIntoWindow);
            return;
        }
        const timer = window.setTimeout(() => onZoomChangeRef.current?.(zoomedIntoWindow), zoomMs);
        return (): void => window.clearTimeout(timer);
    }, [zoomedIntoWindow, zoomMs]);

    // Zooming back out has to scale about the same point it zoomed into, so
    // the last target is kept until another zoom starts.
    const isZoomedIn = zoomedIntoWindow || zoomedIntoTable;
    const lastZoomTargetRef = useRef<ZoomTarget>('window');
    if (zoomedIntoWindow) lastZoomTargetRef.current = 'window';
    else if (zoomedIntoTable) lastZoomTargetRef.current = 'table';
    const zoomTarget = lastZoomTargetRef.current;

    // The zoom scales the room about the target's center, enough for it to
    // cover the screen, and slides that center to the middle of the screen, as
    // if walking up to the window and through it, or sitting down at the table.
    const zoomRect = zoomTarget === 'table' ? scene.tableRect : scene.windowRect;
    const zoomCenterX = zoomRect.left + zoomRect.width / 2;
    const zoomCenterY = zoomRect.top + zoomRect.height / 2;
    const zoomStyle = {
        '--zoom-duration': `${zoomMs}ms`,
        '--zoom-origin-x': `${zoomCenterX * 100}%`,
        '--zoom-origin-y': `${zoomCenterY * 100}%`,
        '--zoom-shift-x': reducedMotion ? '0%' : `${(0.5 - zoomCenterX) * 100}%`,
        '--zoom-shift-y': reducedMotion ? '0%' : `${(0.5 - zoomCenterY) * 100}%`,
        '--zoom-scale': reducedMotion ? 1 : WINDOW_ZOOM_OVERSHOOT / Math.min(zoomRect.width, zoomRect.height),
    } as CSSProperties;

    return (
        <div className={classnames(styles.viewport, className)} style={zoomStyle}>
            <div className={classnames(styles.scene, isZoomedIn && styles.zoomedIn)}>
                <img className={styles.room} src={scene.roomImageUrl} alt="" draggable={false} />
                <div
                    className={classnames(styles.windowGlow, highlightedTab === 'skills' && styles.highlighted)}
                    style={rectStyle(scene.windowRect)}
                />
                {scene.props.map((prop) => (
                    <img
                        key={prop.id}
                        className={classnames(styles.prop, prop.tab !== null && prop.tab === highlightedTab && styles.highlighted)}
                        src={prop.imageUrl}
                        style={rectStyle(prop.rect)}
                        alt=""
                        draggable={false}
                    />
                ))}
                <CharacterPreviewCanvas
                    characterId={characterId}
                    equippedItemIds={equippedItemIds}
                    outfitModifiers={outfitModifiers}
                    itemTiers={itemTiers}
                    // Body and gear always reveal together on load. After that, a
                    // saved character reloads whole on a gear change; creating
                    // re-equips live and frequently as the player tries on
                    // hairstyles/colors, so it takes the lighter `set-equipment`
                    // swap instead of a full character reload per pick. Either
                    // way the player can drag the character to turn it (#270).
                    isEquipmentLoadedWithCharacter={!isCreating}
                    animationsEnabled={animationsEnabled}
                    backdropId="home-workshop"
                    onReady={handleCharacterReady}
                    className={styles.canvas}
                />
            </div>
            {/* In front of the room, it fades in as the room zooms past and settles as if leaning over the table. */}
            <div
                className={classnames(styles.tableTop, zoomedIntoTable && styles.shown, reducedMotion && styles.reducedMotion)}
                style={{ backgroundImage: `url(${scene.tableTopImageUrl})` }}
            />
        </div>
    );
}
